import React, { useState } from 'react';
import { X } from 'lucide-react';

const TermsAndConditionsModal = ({ 
  isOpen, 
  onClose, 
  onAccept, 
  title = "Terms and Conditions",
  acceptButtonText = "I Accept",
  effectiveDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px tolerance
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAcceptChange = (e) => {
    setIsAccepted(e.target.checked);
  };

  const handleAccept = () => {
    if (isAccepted) {
      onAccept(effectiveDate);
    }
  };

  const handleClose = () => {
    setHasScrolledToBottom(false);
    setIsAccepted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              onClick={handleClose}
            >
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                {title}
              </h3>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Effective Date:</strong> {effectiveDate}
                </p>
              </div>

              <div 
                className="max-h-96 overflow-y-auto border border-gray-200 rounded-md p-4 mb-4 bg-gray-50"
                onScroll={handleScroll}
              >
                <div className="text-sm text-gray-700 space-y-4">
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-bold text-gray-900">Engaged Manila â€“ Terms and Conditions</h3>
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Operated by:</strong> Arax Technologies Inc.
                    </p>
                    <p className="text-sm text-gray-600">
                      Welcome to Engaged Manila, an influencer engagement platform powered by Arax Technologies, designed to connect brands with real people to foster authentic engagementâ€”through product testing, content creation, events, and reviews.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      By registering for or using the platform, you agree to the following terms and conditions:
                    </p>
                  </div>

                  <h4 className="font-semibold text-gray-900">1. About Engaged Manila</h4>
                  <p>
                    Engaged Manila is a platform that facilitates meaningful collaboration between brands and individuals (influencers, creators, or everyday users) for the purpose of marketing campaigns, reviews, and engagements. It is a space for brands to receive real, user-generated content and for individuals to participate in creative brand activations.
                  </p>

                  <h4 className="font-semibold text-gray-900">2. Acceptance of Terms</h4>
                  <p>
                    By registering on the Engaged Manila platform, you confirm that you have read, understood, and accepted these Terms and Conditions, including any future modifications. Continued use of the platform constitutes acceptance of the latest version.
                  </p>

                  <h4 className="font-semibold text-gray-900">3. Eligibility</h4>
                  <p>
                    Use of the platform is strictly limited to individuals 18 years of age or older. By registering, you confirm that you meet this age requirement.
                  </p>

                  <h4 className="font-semibold text-gray-900">4. Personal Information and Privacy</h4>
                  <p>
                    You agree to provide accurate personal information upon registration, including but not limited to your full name, contact details, social media accounts, and demographic data. This information is required to match you with campaigns and comply with reporting obligations.
                  </p>
                  <p>
                    Your data will be handled in accordance with the Data Privacy Act of 2012. Engaged Manila is committed to protecting your privacy and complies with applicable data protection regulations.
                  </p>

                  <h4 className="font-semibold text-gray-900">5. Campaign Content Usage</h4>
                  <p>
                    By joining and submitting content for any campaign, you grant Engaged Manila and the brand partner a non-exclusive, royalty-free, perpetual license to use, distribute, publish, and display your content across their digital platforms and marketing channels.
                  </p>

                  <h4 className="font-semibold text-gray-900">6. Communication Guidelines</h4>
                  <p>
                    All questions, concerns, or communications related to campaign participation must be directed to Engaged Manila only. You are not permitted to contact the brand directly. Failure to comply may result in removal from the campaign or suspension of your account.
                  </p>

                  <h4 className="font-semibold text-gray-900">7. Campaign Instructions and Compliance</h4>
                  <p>
                    Each campaign will include detailed instructions, deliverables, and timelines. It is your responsibility to read and understand all campaign guidelines before registering or participating. Failure to follow instructions may result in:
                  </p>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Non-approval of submitted content</li>
                    <li>Forfeiture of incentives</li>
                    <li>Account penalties or removal from the platform</li>
                  </ul>

                  <h4 className="font-semibold text-gray-900">8. Code of Conduct</h4>
                  <p>
                    Engaged Manila is a respectful and inclusive platform. You agree to:
                  </p>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Refrain from using offensive, hateful, or inappropriate language</li>
                    <li>Avoid engaging in harassment, abuse, or defamatory actions</li>
                  </ul>
                  <p>
                    Violations will result in suspension or permanent removal from the platform.
                  </p>

                  <h4 className="font-semibold text-gray-900">9. Limitation of Liability</h4>
                  <p>
                    Engaged Manila shall not be held liable for any direct, indirect, incidental, or consequential damages, including loss of data, loss of opportunity, injury, or disputes arising out of or related to your participation in any campaign.
                  </p>
                  <p>
                    You acknowledge that all campaigns are voluntary, and you participate at your own risk.
                  </p>

                  <h4 className="font-semibold text-gray-900">10. Account Termination</h4>
                  <p>
                    Engaged Manila reserves the right to:
                  </p>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Refuse registration</li>
                    <li>Suspend or cancel any account at its sole discretion</li>
                    <li>Disqualify users for any misconduct, including violation of terms, abuse, non-compliance, or any other behavior deemed inappropriate</li>
                  </ul>
                  <p>
                    All decisions made by Engaged Manila are final and binding. No correspondence will be entered into.
                  </p>

                  <h4 className="font-semibold text-gray-900">11. Platform Responsibility</h4>
                  <p>
                    Engaged Manila is not responsible for the conduct, accuracy, or actions of any other user, including other creators, brands, or campaign participants. You are solely responsible for your interactions on the platform.
                  </p>

                  <h4 className="font-semibold text-gray-900">12. Modifications to Terms</h4>
                  <p>
                    Engaged Manila may revise these Terms and Conditions from time to time. Updated terms will be posted on the platform, and your continued use constitutes acceptance of such changes. Users are encouraged to review the terms periodically.
                  </p>

                  <h4 className="font-semibold text-gray-900">13. Inquiries and Support</h4>
                  <p>
                    For all questions, support, or concerns, please contact us via:
                  </p>
                  <p className="ml-4">
                    Email: <a href="mailto:hola@engagedmanila.com" className="text-blue-600 hover:text-blue-800">hola@engagedmanila.com</a>
                  </p>
                  
                  <div className="border-t border-gray-300 pt-4 mt-6">
                    <p className="font-medium text-gray-900">
                      By completing your registration and using the Engaged Manila platform, you confirm that you have read, understood, and agreed to abide by these Terms and Conditions in full.
                    </p>
                  </div>
                </div>
              </div>

              {!hasScrolledToBottom && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-700">
                    Please scroll through the entire Terms and Conditions document above to continue.
                  </p>
                </div>
              )}

              <div className="mb-4">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={isAccepted}
                    onChange={handleAcceptChange}
                    disabled={!hasScrolledToBottom}
                    className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded disabled:opacity-50"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    I have read and agree to the Terms and Conditions effective as of {effectiveDate}. I understand that this agreement is legally binding.
                  </span>
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={!isAccepted || !hasScrolledToBottom}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {acceptButtonText}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsModal;
